import { useState } from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Slider,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
} from '@mui/material'
import {
  Close,
  Add,
  Star,
  StarBorder,
  Edit,
  Delete,
  ExpandMore,
} from '@mui/icons-material'
import { AdventureSearchRequest } from '@api/types/adventure'
import { useSavedFilters } from '../hooks/useSavedFilters'
import { useAdventureCategories } from '../hooks/useAdventures'
import { useCachedTags } from '@hooks/useCachedTags'
import './editor/styles/TagsPanel.css'

interface AdventureFilterDrawerProps {
  open: boolean
  onClose: () => void
  searchRequest: AdventureSearchRequest
  onSearchRequestChange: (request: AdventureSearchRequest) => void
  selectedFilterId?: string
  onSelectedFilterChange?: (filterId: string) => void
}

export function AdventureFilterDrawer({
  open,
  onClose,
  searchRequest,
  onSearchRequestChange,
  selectedFilterId: externalSelectedFilterId = '',
  onSelectedFilterChange,
}: AdventureFilterDrawerProps) {
  const {
    filters,
    createFilter,
    updateFilter,
    deleteFilter,
    setDefaultFilter,
  } = useSavedFilters()

  const { data: mainCategories, isLoading: categoriesLoading } = useAdventureCategories()

  const [internalSelectedFilterId, setInternalSelectedFilterId] = useState<string>('')

  // Use external selectedFilterId if provided, otherwise use internal state
  const selectedFilterId = onSelectedFilterChange ? externalSelectedFilterId : internalSelectedFilterId
  const setSelectedFilterId = onSelectedFilterChange || setInternalSelectedFilterId

  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [editorDialogOpen, setEditorDialogOpen] = useState(false)
  const [filterName, setFilterName] = useState('')
  const [filterDescription, setFilterDescription] = useState('')
  const [editingFilterId, setEditingFilterId] = useState<string | null>(null)
  const [filterCriteria, setFilterCriteria] = useState<AdventureSearchRequest>({})
  const [activeTab, setActiveTab] = useState(0)
  const [difficultyRange, setDifficultyRange] = useState<number[]>([1, 5])
  const [expandedAdventureCategory, setExpandedAdventureCategory] = useState<string | null>(null)

  const { categories } = useCachedTags()

  const handleCreateNew = () => {
    setFilterName('')
    setFilterDescription('')
    setEditingFilterId(null)
    setFilterCriteria({})
    setDifficultyRange([1, 5])
    setActiveTab(0)
    setNameDialogOpen(true)
  }

  const handleEditFilter = (filterId: string) => {
    const filter = filters.find((f) => f.id === filterId)
    if (filter) {
      setEditingFilterId(filterId)
      setFilterName(filter.name)
      setFilterDescription(filter.description || '')
      setFilterCriteria(filter.filterCriteria)
      setDifficultyRange([
        filter.filterCriteria.minDifficultyLevel || 1,
        filter.filterCriteria.maxDifficultyLevel || 5
      ])
      setActiveTab(0)
      setEditorDialogOpen(true)
    }
  }

  const handleDeleteFilter = async (filterId: string) => {
    try {
      await deleteFilter(filterId)
      if (selectedFilterId === filterId) {
        setSelectedFilterId('')
      }
    } catch (error) {
      console.error('Failed to delete filter:', error)
    }
  }

  const handleSetDefault = async (filterId: string) => {
    try {
      await setDefaultFilter(filterId)
    } catch (error) {
      console.error('Failed to set default filter:', error)
    }
  }

  const handleNameDialogNext = () => {
    setNameDialogOpen(false)
    setActiveTab(0)
    setEditorDialogOpen(true)
  }

  const handleToggleAdventureTag = (tagId: string) => {
    const currentTags = filterCriteria.tagsAdventure || []
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter(id => id !== tagId)
      : [...currentTags, tagId]
    setFilterCriteria({ ...filterCriteria, tagsAdventure: newTags })
  }

  const handleDifficultyRangeChange = (_event: Event, newValue: number | number[]) => {
    const range = newValue as number[]
    setDifficultyRange(range)
    setFilterCriteria({
      ...filterCriteria,
      minDifficultyLevel: range[0],
      maxDifficultyLevel: range[1]
    })
  }

  // Get adventure tag categories (TagType.Adventures = 3)
  const adventureCategories = categories
    .filter(category => category.tagType === 3 && category.isActive)

  // Helper to get chip style
  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  const handleSaveFilter = async () => {
    try {
      const filterData = {
        name: filterName,
        description: filterDescription || undefined,
        filterCriteria: filterCriteria,
      }

      if (editingFilterId) {
        await updateFilter(editingFilterId, filterData)
      } else {
        const newFilter = await createFilter(filterData)
        setSelectedFilterId(newFilter.id)
      }

      setEditorDialogOpen(false)
      setFilterName('')
      setFilterDescription('')
      setFilterCriteria({})
      setEditingFilterId(null)
    } catch (error) {
      console.error('Failed to save filter:', error)
    }
  }

  const handleSelectFilter = (filterId: string) => {
    const filter = filters.find((f) => f.id === filterId)
    if (filter) {
      // Apply the complete filter criteria, preserving user location from current searchRequest
      onSearchRequestChange({
        ...filter.filterCriteria,
        page: filter.filterCriteria.page || 1,
        pageSize: filter.filterCriteria.pageSize || 12,
        userLatitude: searchRequest.userLatitude,
        userLongitude: searchRequest.userLongitude,
      })
      setSelectedFilterId(filterId)
      onClose()
    }
  }

  const handleResetFilter = () => {
    // Reset to default search request, preserving user location
    onSearchRequestChange({
      page: 1,
      pageSize: 12,
      sortBy: 'createdDate',
      sortDirection: 'desc',
      radiusMiles: 50,
      userLatitude: searchRequest.userLatitude,
      userLongitude: searchRequest.userLongitude,
    })
    setSelectedFilterId('')
    onClose()
  }

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Saved Filters
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Filter List */}
          {filters.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No saved filters yet. Create your first filter to get started!
              </Typography>
            </Box>
          ) : (
            <List sx={{ mb: 2 }}>
              {filters.map((filter) => (
                <ListItem
                  key={filter.id}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: selectedFilterId === filter.id ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => handleSelectFilter(filter.id)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {filter.name}
                        </Typography>
                        {filter.isDefault && (
                          <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {filter.description || 'No description'}
                      </Typography>
                    }
                  />
                  <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={filter.isDefault ? 'Remove as default' : 'Set as default'}>
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefault(filter.id)}
                        color={filter.isDefault ? 'primary' : 'default'}
                      >
                        {filter.isDefault ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit filter">
                      <IconButton
                        size="small"
                        onClick={() => handleEditFilter(filter.id)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete filter">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFilter(filter.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </ListItem>
              ))}
            </List>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<Add />}
              onClick={handleCreateNew}
            >
              Create New
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleResetFilter}
              color="warning"
            >
              Reset Filter
            </Button>
            <Button variant="outlined" fullWidth onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Name Dialog */}
      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Filter</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Filter Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Description (optional)"
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleNameDialogNext}
            variant="contained"
            disabled={!filterName.trim()}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Editor Dialog */}
      <Dialog open={editorDialogOpen} onClose={() => setEditorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingFilterId ? 'Edit Filter' : 'Configure Filter Criteria'}</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="General" />
              <Tab label="Tags" />
              <Tab label="Advanced" />
            </Tabs>
          </Box>

          {/* General Tab */}
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
              <TextField
                label="Filter Name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                label="Description (optional)"
                value={filterDescription}
                onChange={(e) => setFilterDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                inputProps={{ maxLength: 500 }}
              />
              <TextField
                label="Search Term"
                value={filterCriteria.searchTerm || ''}
                onChange={(e) => setFilterCriteria({ ...filterCriteria, searchTerm: e.target.value })}
                fullWidth
                inputProps={{ maxLength: 200 }}
                placeholder="Search for adventures by title or description..."
              />

              {/* Adventure Categories */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Adventure Categories
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Select one or more adventure categories to filter by
                </Typography>
                {categoriesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(mainCategories || []).map((category: { id: number; name: string }) => {
                      const isSelected = (filterCriteria.mainCategory as any)?.includes?.(category.id) ||
                                        (Array.isArray(filterCriteria.mainCategory) && filterCriteria.mainCategory.includes(category.id))
                      return (
                        <Chip
                          key={category.id}
                          label={category.name}
                          onClick={() => {
                            const currentCategories = Array.isArray(filterCriteria.mainCategory)
                              ? filterCriteria.mainCategory
                              : (filterCriteria.mainCategory !== undefined && filterCriteria.mainCategory !== null)
                              ? [filterCriteria.mainCategory as number]
                              : []

                            const newCategories = isSelected
                              ? currentCategories.filter((c: number) => c !== category.id)
                              : [...currentCategories, category.id]

                            setFilterCriteria({
                              ...filterCriteria,
                              mainCategory: newCategories.length > 0 ? newCategories : undefined
                            })
                          }}
                          variant={isSelected ? 'filled' : 'outlined'}
                          color={isSelected ? 'primary' : 'default'}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: isSelected ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        />
                      )
                    })}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {Array.isArray(filterCriteria.mainCategory) ? filterCriteria.mainCategory.length : (filterCriteria.mainCategory !== undefined && filterCriteria.mainCategory !== null ? 1 : 0)} categor{Array.isArray(filterCriteria.mainCategory) && filterCriteria.mainCategory.length === 1 ? 'y' : 'ies'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Tags Tab */}
          {activeTab === 1 && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select adventure tags to filter by specific types of adventures
              </Typography>

              {/* Selected Tags Display */}
              {(filterCriteria.tagsAdventure || []).length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Tags ({(filterCriteria.tagsAdventure || []).length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(filterCriteria.tagsAdventure || []).map((tagId) => {
                      // Find the tag and its category
                      let foundTag = null
                      let foundCategory = null
                      for (const category of adventureCategories) {
                        const tag = category.tags?.find(t => t.id === tagId)
                        if (tag) {
                          foundTag = tag
                          foundCategory = category
                          break
                        }
                      }
                      if (!foundTag) return null

                      return (
                        <Chip
                          key={tagId}
                          label={foundTag.displayName}
                          onDelete={() => handleToggleAdventureTag(tagId)}
                          deleteIcon={<Close />}
                          sx={{
                            backgroundColor: foundCategory?.color || '#2196f3',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                            },
                          }}
                        />
                      )
                    })}
                  </Box>
                </Paper>
              )}

              {/* Tag Categories */}
              {(() => {
                const activeCategories = adventureCategories.filter((c) =>
                  c.tags && c.tags.some((t) => t.isActive))

                if (activeCategories.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      No adventure tags available
                    </Typography>
                  )
                }

                // If only one category, show chips directly
                if (activeCategories.length === 1) {
                  const category = activeCategories[0]
                  return (
                    <Box>
                      {category.description && (
                        <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                          {category.description}
                        </Typography>
                      )}
                      <Box className="tag-chips-container">
                        {category.tags
                          ?.filter((t) => t.isActive)
                          .sort((a, b) => {
                            const orderDiff = a.sortOrder - b.sortOrder
                            return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                          })
                          .map((tag) => {
                            const isSelected = (filterCriteria.tagsAdventure || []).includes(tag.id)
                            return (
                              <Chip
                                key={tag.id}
                                label={
                                  <>
                                    {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                                    <span className="tag-label">{tag.displayName}</span>
                                  </>
                                }
                                onClick={() => handleToggleAdventureTag(tag.id)}
                                variant={isSelected ? 'filled' : 'outlined'}
                                size="small"
                                sx={getTagChipStyle(category.color, isSelected)}
                                title={tag.description}
                                className="tag-chip"
                              />
                            )
                          })}
                      </Box>
                    </Box>
                  )
                }

                // Multiple categories, use accordions
                return activeCategories.map((category) => (
                  <Accordion
                    key={category.id}
                    expanded={expandedAdventureCategory === category.id}
                    onChange={(_, isExpanded) => setExpandedAdventureCategory(isExpanded ? category.id : null)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ fontSize: 20 }} />}
                      sx={{
                        minHeight: '40px',
                        '&.Mui-expanded': { minHeight: '40px' },
                        '& .MuiAccordionSummary-content': {
                          my: 0.75,
                          '&.Mui-expanded': { my: 0.75 }
                        }
                      }}
                    >
                      <Box className="category-header">
                        <Box className="category-header-left">
                          <span className="category-icon" style={{ fontSize: '16px' }}>{category.icon || '📁'}</span>
                          <span style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}>
                            {category.displayName}
                          </span>
                        </Box>
                        <Box className="category-header-right">
                          <span
                            className="tag-count-badge"
                            style={{
                              backgroundColor: category.color,
                              color: 'white',
                              fontSize: '0.7rem',
                              padding: '2px 6px',
                              minWidth: '20px',
                              height: '18px',
                            }}
                          >
                            {category.tags?.filter((t) => t.isActive).length || 0}
                          </span>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {category.description && (
                        <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                          {category.description}
                        </Typography>
                      )}
                      <Box className="tag-chips-container">
                        {category.tags
                          ?.filter((t) => t.isActive)
                          .sort((a, b) => {
                            const orderDiff = a.sortOrder - b.sortOrder
                            return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                          })
                          .map((tag) => {
                            const isSelected = (filterCriteria.tagsAdventure || []).includes(tag.id)
                            return (
                              <Chip
                                key={tag.id}
                                label={
                                  <>
                                    {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                                    <span className="tag-label">{tag.displayName}</span>
                                  </>
                                }
                                onClick={() => handleToggleAdventureTag(tag.id)}
                                variant={isSelected ? 'filled' : 'outlined'}
                                size="small"
                                sx={getTagChipStyle(category.color, isSelected)}
                                title={tag.description}
                                className="tag-chip"
                              />
                            )
                          })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))
              })()}
            </Box>
          )}

          {/* Advanced Tab */}
          {activeTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
              {/* Checkboxes */}
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterCriteria.allowGroups || false}
                      onChange={(e) => setFilterCriteria({ ...filterCriteria, allowGroups: e.target.checked })}
                    />
                  }
                  label="Allow Groups"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterCriteria.isRepeatable || false}
                      onChange={(e) => setFilterCriteria({ ...filterCriteria, isRepeatable: e.target.checked })}
                    />
                  }
                  label="Only Repeatable Adventures"
                />
              </FormGroup>

              <Divider />

              {/* Difficulty Range Slider */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Difficulty Range
                </Typography>
                <Box sx={{ px: 2, pt: 1 }}>
                  <Slider
                    value={difficultyRange}
                    onChange={handleDifficultyRangeChange}
                    valueLabelDisplay="auto"
                    min={1}
                    max={5}
                    marks={[
                      { value: 1, label: '1 Easy' },
                      { value: 2, label: '2' },
                      { value: 3, label: '3 Med' },
                      { value: 4, label: '4' },
                      { value: 5, label: '5 Hard' },
                    ]}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Difficulty: {difficultyRange[0]} to {difficultyRange[1]}
                </Typography>
              </Box>

              <Divider />

              {/* Additional Filters */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Min Rating</InputLabel>
                  <Select
                    value={filterCriteria.minRating || ''}
                    label="Min Rating"
                    onChange={(e) => setFilterCriteria({ ...filterCriteria, minRating: e.target.value as number || undefined })}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value={1}>1+ Stars</MenuItem>
                    <MenuItem value={2}>2+ Stars</MenuItem>
                    <MenuItem value={3}>3+ Stars</MenuItem>
                    <MenuItem value={4}>4+ Stars</MenuItem>
                    <MenuItem value={5}>5 Stars</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Radius (miles)"
                  type="number"
                  value={filterCriteria.radiusMiles || ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value)
                    const clampedValue = value > 150 ? 150 : value
                    setFilterCriteria({ ...filterCriteria, radiusMiles: clampedValue || undefined })
                  }}
                  fullWidth
                  inputProps={{ min: 0, max: 150 }}
                  helperText="Maximum 150 miles"
                />
              </Box>

              {/* Sort Options */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={filterCriteria.sortBy || ''}
                    label="Sort By"
                    onChange={(e) => setFilterCriteria({ ...filterCriteria, sortBy: e.target.value })}
                  >
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="createdDate">Created Date</MenuItem>
                    <MenuItem value="title">Title</MenuItem>
                    <MenuItem value="difficulty">Difficulty</MenuItem>
                    <MenuItem value="rating">Rating</MenuItem>
                    <MenuItem value="distance">Distance</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Sort Direction</InputLabel>
                  <Select
                    value={filterCriteria.sortDirection || ''}
                    label="Sort Direction"
                    onChange={(e) => setFilterCriteria({ ...filterCriteria, sortDirection: e.target.value })}
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFilter}
            variant="contained"
            disabled={!filterName.trim()}
          >
            Save Filter
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
